import {useCallback, useState} from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import {ChevronLeft, ChevronRight, Expand} from 'lucide-react';

import ImageFill from '@/components/image/ImageFill';
import {imageWebFromImageWebSchema} from '@/components/image/query';
import type {ImageWeb, ImageWebSchema} from '@/components/image/types';
import {useDotButton} from '@/components/image-gallery/hooks/useDotButton';
import {usePrevNextButtons} from '@/components/image-gallery/hooks/usePrevNextButtons';
import {Lightbox} from '@/components/image-gallery/lightbox';
import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';
import type {KeyedArray} from '@/utils/types';

type Props = {
	images: ImageWebSchema[];
	aspectRatio?: '1:1' | '16:9' | '3:2';
};

export const ImageGallery = ({images, aspectRatio}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const handleOpenFullscreenClick = useCallback(() => {
		setIsOpen(true);
	}, []);

	const prefersReducedMotion = usePrefersReducedMotion();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: images && images.length > 1,
		duration: prefersReducedMotion ? 0 : 20,
	});

	const {selectedIndex, scrollSnaps, onDotButtonClick} =
		useDotButton(emblaApi);

	const {
		prevBtnDisabled,
		nextBtnDisabled,
		onPrevButtonClick,
		onNextButtonClick,
	} = usePrevNextButtons(emblaApi);

	let imageAspectRatio = 3 / 2;

	switch (aspectRatio) {
		case '1:1':
			imageAspectRatio = 1;
			break;
		case '16:9':
			imageAspectRatio = 16 / 9;
			break;
		case '3:2':
			imageAspectRatio = 3 / 2;
			break;
	}

	if (!images || images.length === 0) {
		return null;
	}

	const carouselImages: KeyedArray<ImageWeb> = [];

	for (const sanityImage of images) {
		if (!sanityImage.asset?._ref) {
			continue;
		}

		const imageWeb = imageWebFromImageWebSchema(
			sanityImage,
			imageAspectRatio,
		);

		if (!imageWeb) {
			continue;
		}

		carouselImages.push({
			_key: sanityImage.asset._ref,
			...imageWeb,
		});
	}

	const currentImage = carouselImages[selectedIndex];

	if (!carouselImages || carouselImages.length === 0) {
		return null;
	}

	return (
		<div>
			<div
				className={clsx([
					'relative flex',
					'overflow-hidden',
					'rounded-xl',
					aspectRatio === '1:1' && 'aspect-square',
					aspectRatio === '3:2' && 'aspect-[calc(3/2)]',
					aspectRatio === '16:9' && 'aspect-video',
				])}
			>
				<div
					ref={emblaRef}
					className="flex shrink-0 grow overflow-hidden"
				>
					<div className="flex grow touch-pan-y touch-pinch-zoom">
						{carouselImages.map((image) => (
							<div
								key={image._key}
								className="relative shrink-0 grow-0 basis-full"
							>
								<ImageFill
									{...image.image}
									alt={image.altText ?? ''}
									className="object-cover"
								/>
							</div>
						))}
					</div>
				</div>

				{images.length > 1 && (
					<>
						<button
							type="button"
							onClick={onPrevButtonClick}
							disabled={prevBtnDisabled}
							aria-label="Forrige bilde"
							className={clsx(
								'absolute',
								'top-1/2',
								'left-1.5',
								'-translate-y-1/2',
								'p-2',
								'disabled:opacity-50',
								'bg-black/70',
								'rounded-full',
								'cursor-pointer',
								'disabled:pointer-events-none',
								'text-white',
							)}
						>
							<ChevronLeft width={32} height={32} aria-hidden />
						</button>

						<button
							type="button"
							onClick={onNextButtonClick}
							disabled={nextBtnDisabled}
							aria-label="Neste bilde"
							className={clsx(
								'absolute',
								'top-1/2',
								'right-1.5',
								'-translate-y-1/2',
								'p-2',
								'disabled:opacity-50',
								'bg-black/70',
								'rounded-full',
								'cursor-pointer',
								'disabled:pointer-events-none',
								'text-white',
							)}
						>
							<ChevronRight width={32} height={32} aria-hidden />
						</button>

						<div className="absolute inset-x-0 bottom-4 flex flex-wrap items-center justify-center gap-2">
							{scrollSnaps.map((_, index) => (
								<button
									type="button"
									key={index}
									onClick={() => onDotButtonClick(index)}
									aria-label={`Bilde ${index + 1}`}
									className={clsx(
										'relative',
										'h-1.5',
										'w-14',
										'rounded-full',
										'bg-black',
										'opacity-60',
										'before:absolute',
										'before:inset-[-8px]',
										"before:content-['']",
										'hover:opacity-100',
										'focus:opacity-100',
										'disabled:opacity-30',
										'shadow-[0px_0px_15px_rgba(255,255,255,0.8)]',
										selectedIndex === index
											? 'opacity-100'
											: '',
									)}
								/>
							))}
						</div>
					</>
				)}
				<div
					className={clsx('absolute', 'right-1.5', 'top-1.5', 'z-10')}
				>
					<button
						onClick={handleOpenFullscreenClick}
						className={clsx(
							'bg-black/70',
							'p-2',
							'rounded-full',
							'text-white',
							'cursor-pointer',
						)}
						aria-label="Åpne bildegalleri i fullskjerm"
					>
						<Expand aria-hidden />
					</button>
				</div>
			</div>

			{(currentImage.caption || currentImage.credit) && (
				<div
					className={clsx(
						'flex',
						'flex-wrap',
						'justify-between',
						'gap-x-4',
						'gap-y-1',
						'mt-3',
						'text-body-short-xs',
					)}
				>
					{currentImage.caption && (
						<span className={clsx('text-neutral-text-subtle')}>
							{currentImage.caption}
						</span>
					)}
					{currentImage.credit && (
						<span className={clsx('text-primary-text-subtle')}>
							{currentImage.credit}
						</span>
					)}
				</div>
			)}

			<Lightbox
				setIsOpen={setIsOpen}
				startIndex={selectedIndex}
				isOpen={isOpen}
				images={carouselImages}
			/>
		</div>
	);
};
